import { useCallback, useEffect, useRef, useState } from 'react';

import Skeleton from '@/components/ui/skeleton/skeleton';
import { ProfileAvatar, useProfilesData } from '@/features/user';
import { useSelectProfile } from '@/features/user/api/queries';
import { useScrollToTop } from '@/hooks/use-scroll-to-top';
import { usePageView } from '@/lib/analytics';

export default function ForceSelectProfile() {
  usePageView('Force Select Profile');
  useScrollToTop();

  const { studentProfiles, selectedProfileId, isLoading, isError } =
    useProfilesData();

  const selectProfileMutation = useSelectProfile();

  const [selectedId, setSelectedId] = useState<string>('null');
  const hasAutoSelected = useRef(false);

  const handleProfileSelect = useCallback(
    (profileId: string) => {
      setSelectedId(profileId);
      selectProfileMutation.mutate(profileId);
    },
    [selectProfileMutation]
  );

  useEffect(() => {
    if (
      studentProfiles.length === 1 &&
      studentProfiles?.[0]?.id &&
      !hasAutoSelected.current
    ) {
      hasAutoSelected.current = true;
      handleProfileSelect(studentProfiles[0].id);
    }
  }, [studentProfiles, handleProfileSelect]);

  if (isLoading || selectProfileMutation.isPending) {
    return (
      <div className="bg-magical-moonlight-white flex flex-col items-center justify-center px-4 py-8">
        <div className="w-full max-w-2xl text-center">
          <Skeleton
            variant="secondary"
            size="xl"
            width="lg"
            className="mx-auto mb-12"
          />

          <div className="flex flex-wrap justify-center gap-8">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex flex-col items-center gap-2">
                <Skeleton
                  shape="circle"
                  className="size-20"
                  variant="secondary"
                />
                <Skeleton size="sm" width="md" variant="secondary" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="w-full h-60 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center">
          <h1 className="text-2xl font-bold text-magical-moonlight-d1 capitalize">
            Error loading profiles!
          </h1>
          <p className="text-sm text-magical-moonlight-d1 capitalize">
            Please try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-magical-moonlight-white flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-2xl text-center">
        <h1 className="text-2xl font-bold font-quicksand text-magical-moonlight-d4 mb-12">
          Choose your profile
        </h1>

        <div className="flex flex-wrap justify-center gap-8">
          {studentProfiles.map(profile => (
            <ProfileAvatar
              key={profile.id}
              profile={profile}
              onSelect={handleProfileSelect}
              loading={
                selectedId === profile.id && selectProfileMutation.isPending
              }
              selected={selectedProfileId === profile.id}
              disabled={selectProfileMutation.isPending}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
